import { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { MessageSquareReply, Loader2, Check, X, GraduationCap, Star } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const STATUS = {
  pending: { label: "Bekliyor", cls: "bg-amber-100 text-amber-700" },
  approved: { label: "Onaylandı", cls: "bg-emerald-100 text-emerald-700" },
  rejected: { label: "Reddedildi", cls: "bg-red-100 text-red-700" },
};
const RESULT = { done: "Yapıldı", partial: "Kısmen Yapıldı", missed: "Yapılamadı" };
const isVideo = (u) => /\.(mp4|mov|webm|avi|m4v)$/i.test(u || "");
const mediaSrc = (u) => {
  if (!u) return u;
  if (u.startsWith("http")) return u;
  const token = localStorage.getItem("renka_token");
  const sep = u.includes("?") ? "&" : "?";
  return `${BACKEND}${u}${token ? `${sep}auth=${token}` : ""}`;
};

function ReviewDialog({ item, onClose, onDone }) {
  const [rating, setRating] = useState(item?.rating || 0);
  const [result, setResult] = useState(item?.review_result || "done");
  const [comment, setComment] = useState(item?.review_comment || "");
  const [saving, setSaving] = useState(false);
  const submit = async (status) => {
    setSaving(true);
    try {
      await api.put(`/feedback/${item.id}`, { status, rating: rating || undefined, review_result: result, review_comment: comment });
      toast.success(status === "approved" ? "Onaylandı" : "Reddedildi");
      onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-2xl sm:max-w-md" data-testid="review-dialog">
        <DialogHeader><DialogTitle className="font-heading text-xl">Öğretmen Geri Bildirimi</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2"><Label>Puan</Label>
            <div className="flex gap-1">
              {[1, 2, 3, 4, 5].map((i) => (
                <button key={i} type="button" onClick={() => setRating(i)} data-testid={`star-${i}`}>
                  <Star className={`h-7 w-7 ${i <= rating ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />
                </button>
              ))}
            </div>
          </div>
          <div className="space-y-2"><Label>Sonuç</Label>
            <div className="flex gap-2">
              {Object.entries(RESULT).map(([k, v]) => (
                <button key={k} type="button" onClick={() => setResult(k)} data-testid={`result-${k}`}
                  className={`flex-1 rounded-xl border py-2 text-sm font-semibold ${result === k ? "border-violet-500 bg-violet-50 text-violet-700" : "border-slate-200 text-slate-500"}`}>{v}</button>
              ))}
            </div>
          </div>
          <div className="space-y-2"><Label>Yorum</Label><Textarea value={comment} onChange={(e) => setComment(e.target.value)} data-testid="review-comment" placeholder="Harika ilerleme! ..." /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => submit("rejected")} disabled={saving} data-testid="review-reject" className="rounded-xl text-red-600 hover:bg-red-50">Reddet</Button>
          <Button onClick={() => submit("approved")} disabled={saving} data-testid="review-approve" className="rounded-xl bg-emerald-600 text-white hover:bg-emerald-700">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Onayla & Gönder"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Feedback() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending");
  const [reviewTarget, setReviewTarget] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const q = filter === "all" ? "" : `?status=${filter}`;
      const { data } = await api.get(`/feedback${q}`);
      setItems(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

  const chips = [["pending", "Bekleyen"], ["approved", "Onaylı"], ["rejected", "Reddedilen"], ["all", "Tümü"]];

  return (
    <DashboardLayout>
      <div className="p-4 sm:p-6 lg:p-10" data-testid="feedback-page">
        <header className="mb-6">
          <p className="text-xs font-semibold uppercase tracking-[0.05em] text-slate-500">Aile İletişimi</p>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900 sm:text-4xl">Geri Bildirimler</h1>
          <p className="mt-2 text-sm text-slate-600">Velilerin gönderdiği fotoğraf/video ve yorumları incele ve onayla.</p>
        </header>

        <div className="mb-6 flex flex-wrap gap-2">
          {chips.map(([k, label]) => (
            <button key={k} onClick={() => setFilter(k)} data-testid={`feedback-filter-${k}`}
              className={`rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors ${filter === k ? "bg-violet-600 text-white" : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"}`}>{label}</button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-violet-600" /></div>
        ) : items.length === 0 ? (
          <Card className="rounded-2xl border-dashed p-16 text-center text-slate-500" data-testid="feedback-empty"><MessageSquareReply className="mx-auto mb-3 h-10 w-10 text-slate-300" />Bu kategoride geri bildirim yok.</Card>
        ) : (
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            {items.map((f) => {
              const st = STATUS[f.status] || STATUS.pending;
              return (
                <Card key={f.id} data-testid={`feedback-card-${f.id}`} className="rounded-2xl border-slate-200/60 p-5 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
                  <div className="mb-3 flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 text-sm">
                      <GraduationCap className="h-4 w-4 text-violet-500" />
                      <span className="font-semibold text-slate-800">{f.student_name || "Öğrenci"}</span>
                      <span className="text-slate-400">·</span>
                      <span className="text-slate-500">{f.parent_name}</span>
                    </div>
                    <Badge className={`rounded-full ${st.cls}`}>{st.label}</Badge>
                  </div>
                  {f.task_title && <p className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-400">Görev: {f.task_title}</p>}
                  {f.comment && <p className="mb-3 text-sm text-slate-700">{f.comment}</p>}
                  {f.media_urls?.length > 0 && (
                    <div className="mb-3 grid grid-cols-2 gap-2">
                      {f.media_urls.map((u, i) => isVideo(u) ? (
                        <video key={i} src={mediaSrc(u)} controls className="h-32 w-full rounded-lg bg-black object-cover" data-testid={`feedback-media-${f.id}-${i}`} />
                      ) : (
                        <img key={i} src={mediaSrc(u)} alt="feedback" className="h-32 w-full rounded-lg object-cover" data-testid={`feedback-media-${f.id}-${i}`} />
                      ))}
                    </div>
                  )}
                  {f.status === "pending" ? (
                    <Button size="sm" onClick={() => setReviewTarget(f)} data-testid={`review-feedback-${f.id}`} className="w-full rounded-lg bg-violet-600 text-white hover:bg-violet-700"><MessageSquareReply className="mr-1 h-4 w-4" /> İncele & Geri Bildir</Button>
                  ) : (
                    <div className="mt-1 rounded-xl bg-slate-50 p-3">
                      {f.review_result && <span className="mb-1 inline-block rounded-full bg-slate-200 px-2.5 py-0.5 text-xs font-semibold text-slate-700">{RESULT[f.review_result]}</span>}
                      {f.rating ? (
                        <div className="flex gap-0.5">{[1, 2, 3, 4, 5].map((i) => <Star key={i} className={`h-4 w-4 ${i <= f.rating ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />)}</div>
                      ) : null}
                      {f.review_comment && <p className="mt-1 text-sm text-slate-700">{f.review_comment}</p>}
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        )}
      </div>
      {reviewTarget && <ReviewDialog item={reviewTarget} onClose={() => setReviewTarget(null)} onDone={() => { setReviewTarget(null); load(); }} />}
    </DashboardLayout>
  );
}
